import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck, Phone, Mail, Clock, Shield, Zap, MapPin, ArrowRight,
  CheckCircle2, Star, ChevronDown, Sofa, Home, PackageCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1769972557854-7eae6f95585b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxmdXJuaXR1cmUlMjBkZWxpdmVyeSUyMHNvZmElMjBtb3ZpbmclMjBob21lfGVufDB8fHx8MTc3NjQ0ODEyM3ww&ixlib=rb-4.1.0&q=85";
const VAN_IMG = "https://images.unsplash.com/photo-1742858492775-8f58f645aa12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxmdXJuaXR1cmUlMjBkZWxpdmVyeSUyMHNvZmElMjBtb3ZpbmclMjBob21lfGVufDB8fHx8MTc3NjQ0ODEyM3ww&ixlib=rb-4.1.0&q=85";
const LONDON_IMG = "https://images.unsplash.com/photo-1764416166626-7f146bd8e36d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxMb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwdmFuJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc3NjQ0NjgxNXww&ixlib=rb-4.1.0&q=85";

const FAQ_DATA = [
  {
    q: "How much does furniture delivery cost in London?",
    a: "Every furniture delivery quote is generated instantly based on distance, vehicle and item requirements — calculated from your pickup and delivery postcodes. The price shown at checkout is what you pay; there are no surcharges added afterwards. Use our instant quote calculator for your exact dedicated price."
  },
  {
    q: "Can you deliver a single item of furniture?",
    a: "Absolutely. We regularly deliver single items like sofas, beds, wardrobes, dining tables, and desks. You do not need a full house move to use our service. One item or twenty, we are happy to help."
  },
  {
    q: "Do you assemble furniture after delivery?",
    a: "Our core service is collection and delivery. While our drivers can help position heavy items in your chosen room, we do not offer full assembly services. We recommend arranging a specialist fitter if assembly is needed."
  },
  {
    q: "Will you carry furniture upstairs?",
    a: "Yes, our drivers will help move items to the correct room within your property, including carrying up or down stairs where it is safe to do so. For very heavy items or difficult access (narrow staircases, no lift), please let us know when booking so we can plan accordingly."
  },
  {
    q: "Can you collect furniture from a shop or private seller?",
    a: "Yes, we regularly collect furniture from retail stores like IKEA, DFS, and John Lewis, as well as from private sellers on Gumtree, Facebook Marketplace, and eBay. Just give us the pickup and delivery addresses and we handle the rest."
  },
  {
    q: "Is my furniture protected during transit?",
    a: "Yes. All items are covered by our goods-in-transit insurance. Our drivers use blankets and straps to secure furniture in the van and prevent movement or scratching during transport. Fragile or antique items receive extra attention."
  }
];

const AREAS = [
  "Central London", "East London", "West London", "North London", "South London",
  "Greenwich", "Lewisham", "Croydon", "Sutton", "Kingston",
  "Merton", "Wandsworth", "Lambeth", "Southwark", "Hackney",
  "Islington", "Camden", "Brent", "Ealing", "Hillingdon",
  "Harrow", "Barnet", "Enfield", "Redbridge", "Havering"
];

function FAQItem({ question, answer }) {
  return (
    <details className="group border border-slate-200 rounded-lg" data-testid="faq-item">
      <summary className="flex items-center justify-between cursor-pointer p-5 text-left font-semibold text-slate-900 hover:text-orange-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
        {question}
        <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
      </summary>
      <div className="px-5 pb-5 text-slate-600 leading-relaxed">{answer}</div>
    </details>
  );
}

export default function FurnitureDeliveryLondonPage() {
  const navigate = useNavigate();

  const setMeta = useCallback((attr, attrVal, content) => {
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
    el.setAttribute("content", content);
    el.setAttribute("data-page", "furniture-delivery-london");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageTitle = "Furniture Delivery London | Dedicated, Insured, Same-Day | Transport Anything 247";
    const originalTitle = document.title;
    document.title = pageTitle;
    const titleTimer = setTimeout(() => { document.title = pageTitle; }, 100);

    setMeta("name", "description", "Professional furniture delivery London. Sofas, beds, wardrobes delivered same day with dedicated, fully-insured transport. Instant transparent quotes online. Call +44 7757 133163.");
    setMeta("name", "keywords", "furniture delivery London, sofa delivery London, furniture collection London, same day furniture delivery, professional furniture delivery London");
    setMeta("property", "og:title", "Furniture Delivery London | Transport Anything 247");
    setMeta("property", "og:description", "Professional furniture delivery in London. Sofas, beds, wardrobes delivered same day with dedicated transport, fully insured, instant transparent quotes.");
    setMeta("property", "og:type", "website");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://transportanything247.co.uk/services/furniture-delivery-london");
    canonical.setAttribute("data-page", "furniture-delivery-london");

    return () => { document.title = originalTitle; clearTimeout(titleTimer); document.querySelectorAll('[data-page="furniture-delivery-london"]').forEach(el => el.remove()); };
  }, [setMeta]);

  const structuredData = {
    "@context": "https://schema.org", "@type": "Service", "name": "Furniture Delivery London",
    "provider": { "@type": "LocalBusiness", "name": "Transport Anything 247", "telephone": "+447757133163", "email": "customers@trasportanything247.com", "url": "https://transportanything247.co.uk", "areaServed": { "@type": "City", "name": "London" }, "openingHours": "Mo-Su 00:00-23:59" },
    "description": "Professional furniture delivery service in London. Sofas, beds, wardrobes delivered same day with dedicated, fully-insured transport and instant transparent quotes.", "areaServed": "London, United Kingdom", "serviceType": "Furniture Delivery"
  };
  const faqStructuredData = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } }))
  };

  return (
    <div className="min-h-screen" data-testid="furniture-delivery-london-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#calculator" className="nav-link">Get Quote</a>
              <a href="/#services" className="nav-link">Services</a>
              <a href="/#contact" className="nav-link">Contact</a>
              <a href="/blog" className="nav-link">Blog</a>
            </div>
            <a href="tel:+447757133163" className="hidden sm:flex items-center gap-2 text-orange-500 font-medium" data-testid="nav-phone">
              <Phone className="h-4 w-4" /><span>+44 7757 133163</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Man carrying furniture for delivery in London neighbourhood" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <p className="text-orange-400 font-medium text-sm tracking-wider uppercase mb-4">Careful Handling Guaranteed</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Furniture Delivery <span className="text-orange-500">London</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
              Need furniture collected and delivered in London? From a single sofa to a full house of items, we move it all with <strong className="text-white">dedicated, fully-insured transport</strong>. Same day service, instant transparent quotes online, available 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-8 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="hero-get-quote-btn">
                Get an Instant Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-medium px-8 py-3 rounded-md transition-colors" data-testid="hero-call-btn">
                <Phone className="h-5 w-5" /> Call Now
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-orange-400" /> Fully Insured</span>
              <span className="flex items-center gap-2"><Sofa className="h-4 w-4 text-orange-400" /> Any Furniture Type</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-400" /> Same Day Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-slate-900 py-5 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Star, label: "5-Star Rated Service" },
              { icon: Shield, label: "Items Fully Insured" },
              { icon: Home, label: "Room-of-Choice Delivery" },
              { icon: MapPin, label: "All London Postcodes" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                <item.icon className="h-4 w-4 text-orange-500 flex-shrink-0" /><span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Furniture Delivery Services in London
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              Whether you have bought a new sofa online or you are moving your entire bedroom set across town, our furniture delivery service handles it with care.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sofa, title: "Sofa & Living Room", desc: "Sofas, armchairs, coffee tables, TV units, and shelving delivered safely. We use blankets and straps to protect upholstery and prevent scratches during transit." },
              { icon: Home, title: "Bedroom Furniture", desc: "Beds, mattresses, wardrobes, chest of drawers, and dressing tables collected and delivered. We handle heavy and awkward items that standard couriers refuse." },
              { icon: PackageCheck, title: "Online Purchase Collection", desc: "Bought furniture from IKEA, Gumtree, Facebook Marketplace, or eBay? We collect from the seller or store and deliver directly to your home the same day." },
              { icon: MapPin, title: "Office Furniture", desc: "Desks, filing cabinets, office chairs, and conference tables moved between offices or from suppliers. Minimal disruption to your working day." },
              { icon: Shield, title: "Antique & Fragile Items", desc: "Antique furniture, glass-topped tables, and delicate items get extra care. Protective wrapping, careful handling, and experienced drivers who understand the value." },
              { icon: Truck, title: "Full House Clearance", desc: "Moving multiple rooms of furniture? We can handle full loads efficiently, making multiple trips if needed. Get a single quote for everything and let us take care of logistics." },
            ].map((service, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                  <service.icon className="h-6 w-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center text-slate-600 text-sm leading-relaxed max-w-2xl mx-auto">
            <p>
              Need other transport? Explore our <a href="/services/man-and-van-london" className="text-orange-500 hover:underline font-medium">man and van</a>, <a href="/services/car-transport-london" className="text-orange-500 hover:underline font-medium">car transport</a>, and <a href="/services/courier-service-london" className="text-orange-500 hover:underline font-medium">courier</a> services across London.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Why Choose Us for Furniture Delivery in London?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Moving furniture requires strength, care, and the right vehicle. Here is why Londoners trust Transport Anything 247 with their prized possessions:
              </p>
              <div className="space-y-4">
                {[
                  { title: "Careful, Experienced Handlers", desc: "Our drivers know how to manoeuvre bulky furniture through tight doorways, narrow hallways, and up staircases without damaging your items or your property." },
                  { title: "Room-of-Choice Placement", desc: "We do not just dump items at your front door. Our team carries your furniture to the room you want it in and positions it where you need it." },
                  { title: "Protective Transit", desc: "Furniture blankets, straps, and careful loading techniques keep your items secure and scratch-free throughout the journey. Upholstery, wood, and glass all get appropriate protection." },
                  { title: "Transparent Instant Pricing", desc: "Instant quotes calculated from your postcodes, vehicle and item type. No call-out fees, no staircase charges, no nasty surprises — the price you see online is the price you pay." },
                  { title: "Same Day Availability", desc: "Need that sofa delivered today? We operate 24/7 and can often arrange same day furniture collection and delivery across London." },
                  { title: "Full Insurance Cover", desc: "Comprehensive goods-in-transit insurance protects every item from the moment we pick it up to the second it is placed in your home." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={VAN_IMG} alt="Delivery van ready for furniture collection" className="w-full h-96 object-cover rounded-xl shadow-lg" />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-xl p-5 shadow-lg hidden md:block" data-testid="furniture-trust-badge">
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>100%</p>
                <p className="text-sm font-medium">Fully Insured</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 md:py-24 bg-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Furniture Delivery Across London
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We deliver furniture to every postcode in the Greater London area. From high-rise flats in Canary Wharf to terraced houses in Clapham, our drivers handle the access challenges that come with London properties.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AREAS.map((area, i) => (
                  <span key={i} className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">{area}</span>
                ))}
              </div>
              <p className="text-slate-500 text-sm">
                We also deliver nationwide. <a href="tel:+447757133163" className="text-orange-500 hover:underline font-medium">Call us</a> for deliveries outside London.
              </p>
            </div>
            <div><img src={LONDON_IMG} alt="London residential street" className="w-full h-80 object-cover rounded-xl shadow-lg" /></div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Need Furniture Delivered in London?
          </h2>
          <p className="text-slate-300 mb-8 text-base max-w-2xl mx-auto">
            Get an instant delivery quote. Enter your postcodes, select "Furniture" as the item type, and see your price in seconds. No registration, no waiting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-10 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="cta-get-quote-btn">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-10 py-3 rounded-md transition-colors border border-white/20" data-testid="cta-call-btn">
              <Phone className="h-5 w-5" /> +44 7757 133163
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Furniture Delivery FAQs</h2>
            <p className="text-slate-600 text-base">Common questions about our London furniture delivery service.</p>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((item, i) => (<FAQItem key={i} question={item.q} answer={item.a} />))}
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Affordable Furniture Delivery Across London</h2>
          <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed space-y-4">
            <p>Buying furniture in London is easy. Getting it home is the hard part. Many retailers charge steep delivery fees, offer narrow time windows, and will not carry items past the front door. Private sellers on Gumtree and Facebook Marketplace rarely deliver at all. That is where Transport Anything 247 comes in.</p>
            <p>We bridge the gap between where your furniture is and where you want it to be. Our drivers collect from any London address, whether that is a retail warehouse in Park Royal, a flat in Bermondsey, or a private home in Hampstead, and deliver it straight to the room of your choice at the destination.</p>
            <p>Our vehicles are equipped with furniture blankets, ratchet straps, and trolleys to ensure safe transit. We handle everything from compact bedside tables to king-size beds and L-shaped sectional sofas. Nothing is too big, too heavy, or too awkward for our experienced team.</p>
            <p>Pricing is straightforward and transparent — every quote is generated instantly based on distance, vehicle and item type. The price shown at checkout is the price you pay, with no hidden surcharges and no call-out fees. Because we offer same day service, you are not waiting days for a delivery slot either.</p>
            <p>We are more than just a furniture service. Explore our full range including <a href="/services/man-and-van-london" className="text-orange-500 hover:underline">man and van</a> for house moves, <a href="/services/car-transport-london" className="text-orange-500 hover:underline">car transport</a> for vehicle shipping, and <a href="/services/courier-service-london" className="text-orange-500 hover:underline">same day courier</a> for parcels and packages.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
              </div>
              <p className="text-slate-400 text-sm">Professional furniture delivery in London and across the UK. Affordable, careful, available 24/7.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Our Services</h4>
              <div className="space-y-2">
                <a href="/#calculator" className="footer-link block">Get Quote</a>
                <a href="/services/man-and-van-london" className="footer-link block">Man and Van London</a>
                <a href="/services/car-transport-london" className="footer-link block">Car Transport London</a>
                <a href="/services/courier-service-london" className="footer-link block">Courier Service London</a>
                <a href="/services/furniture-delivery-london" className="footer-link block text-orange-400">Furniture Delivery London</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p>Phone: <a href="tel:+447757133163" className="text-orange-400 hover:text-orange-300">+44 7757 133163</a></p>
                <p>Email: <a href="mailto:customers@trasportanything247.com" className="text-orange-400 hover:text-orange-300">customers@trasportanything247.com</a></p>
                <p>Hours: 24/7 - Always Available</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Transport Anything 247. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
